/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Button, TextInput, TextArea } from 'grommet';
import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';

import './styles/styles.css';
import SEO from './SEO';
import fivebyfive from '../assets/images/Units/5x5 - square.png';
import fivebyten from '../assets/images/Units/5x10 - square.png';
import { createLead } from '../graphql/mutations';
import { FACILITIES, FACILITY_KEYS, facilityIdFor } from '../utils/facilities';

// --- Inline styles (JS-friendly, no TS types) ---

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle = {
  width: '100%',
  maxWidth: 480,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.6)',
  color: '#F5F5FA',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const modalTitle = {
  fontSize: 20,
  fontWeight: 600,
  marginBottom: 8,
};

const modalSubtitle = {
  fontSize: 14,
  opacity: 0.85,
  marginBottom: 16,
};

const modalFieldWrapper = {
  marginBottom: 12,
};

const subtleButton = {
  borderRadius: 999,
  padding: '8px 16px',
  border: '1px solid rgba(255,255,255,0.4)',
  backgroundColor: 'transparent',
  color: '#F5F5FA',
  fontSize: 13,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  cursor: 'pointer',
};

// White chevron, hand-rolled SVG so it shows on dark gradient
const chevronUrl =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='%23F5F5FA' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M4 6l4 4 4-4'/></svg>\")";

const regionSelectStyle = (selectedKey) => ({
  width: '100%',
  padding: '11px 12px',
  paddingRight: 38,
  borderRadius: 4,
  border: '1px solid rgba(0,0,0,0.33)',
  backgroundColor: 'transparent',
  backgroundImage: chevronUrl,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '14px',
  color: selectedKey ? '#F5F5FA' : 'rgba(245,245,250,0.55)',
  fontFamily: 'Raleway, sans-serif',
  fontSize: '16px',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  cursor: 'pointer',
  boxSizing: 'border-box',
  outline: 'none',
});

// Native option dropdowns can't inherit dark theme — keep them readable on white.
const regionOptionStyle = {
  color: '#222',
  backgroundColor: '#fff',
};

const pricingWrapperStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  justifyContent: 'center',
  marginTop: 16,
};

const pricingCardStyle = {
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.08)',
};

const heroTitleStyle = {
  marginTop: 8,
  marginBottom: 8,
};

const compareCardOuter = {
  marginTop: 32,
  marginBottom: 48,
  display: 'flex',
  justifyContent: 'center',
};

const compareCardInner = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
  fontFamily: 'Raleway, sans-serif',
};

const tableWrapper = {
  overflowX: 'auto',
  marginTop: 12,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '16px',
};

const thStyle = {
  padding: '12px',
  borderBottom: '1px solid rgba(255,255,255,0.18)',
  textAlign: 'left',
  fontWeight: 600,
  backgroundColor: 'rgba(58, 31, 82, 0.9)',
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.08)',
};

const binsRowStyle = {
  backgroundColor: 'rgba(64, 199, 120, 0.09)',
};

const badgeStyle = {
  display: 'inline-block',
  marginLeft: 8,
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  backgroundColor: 'rgba(64, 199, 120, 0.16)',
  color: '#C1FFD8',
};

const GOOGLE_ADS_SEND_TO = 'AW-16933443005/N1I1CJiwuPkbEL2rv4o_';

const reportReservationConversion = (reservationId) => {
  if (typeof window === 'undefined') return;
  if (!window.gtag) return;

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SEND_TO,
    transaction_id: reservationId, // recommended for dedupe
  });
};

const Download = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    moveInDate: '',
    message: '',
    region: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const sendEmail = () => {
    // region is already in formData; unitSize and submittedVia are added here so
    // the lead email shows the requested location, unit size, and that it came
    // from the web app (vs the mobile app).
    const payload = {
      ...formData,
      unitSize: selectedUnit || '',
      submittedVia: 'web',
    };
    return fetch(
      'https://clrpa7xufk.execute-api.us-east-2.amazonaws.com/default/sendLead',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        console.log('Email sent successfully:', data);
        return data;
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        throw error;
      });
  };

  const createLeadRecord = async () => {
    const digits = (formData.phone || '').replace(/\D/g, '');
    const phoneE164 = digits.length === 10 ? `+1${digits}` : (formData.phone || null);
    const noteParts = [];
    if (selectedUnit) noteParts.push(`Unit: ${selectedUnit}`);
    noteParts.push('Submitted via web app');
    if (formData.message?.trim()) noteParts.push(formData.message.trim());

    try {
      await API.graphql({
        query: createLead,
        variables: {
          input: {
            id: uuidv4(),
            facilityID: facilityIdFor(formData.region),
            name: formData.name.trim(),
            email: formData.email.trim() || null,
            phone: phoneE164,
            anticipatedDate: formData.moveInDate?.trim() || null,
            notes: noteParts.join(' — ') || null,
            dateCreated: new Date().toISOString().slice(0, 10),
            status: 'new',
          },
        },
        authMode: 'AWS_IAM',
      });
    } catch (err) {
      console.error('Failed to create Lead record:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openModal = (unitSize) => {
    setSelectedUnit(unitSize);
    setShowModal(true);
    setIsSubmitted(false);
  };

  const goToSignup = () => {
    sessionStorage.setItem('prefill_email', formData.email);
    window.location.href = '/signup';
  };

  useEffect(() => {
    if (!isSubmitted) return;
    const timer = setTimeout(goToSignup, 3000);
    return () => clearTimeout(timer);
  }, [isSubmitted]);

  const closeModal = () => {
    if (isSubmitting) return;
    setShowModal(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    console.log('Form submitted:', formData);

    // Run both in parallel; the email Lambda is the user-visible success path,
    // the Lead record is best-effort (errors logged inside createLeadRecord).
    Promise.all([sendEmail(), createLeadRecord()])
      .then(([emailJson]) => {
        console.log('Success:', emailJson);
        setIsSubmitted(true);

        const reservationId =
          emailJson?.reservationId ||
          `${Date.now()}_${(formData.phone || '').replace(/\D/g, '')}`;

        reportReservationConversion(reservationId);
      })
      .catch((err) => {
        console.error('Error:', err.message, err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div>
      <SEO
        title="Valet Storage Pricing San Francisco | Bins Storage"
        description="Reserve a 5x5 or 5x10 valet storage unit in the SF Bay Area starting at $30/month. Free pickup. No truck needed. Book online in minutes."
        path="/Download"
      />
      <section className="download" id="download">
        <div className="graphicontainer" />
        <h2 className="c" style={heroTitleStyle}>
          Reserve Storage Today!
        </h2>

        <div className="pricing-container" style={pricingWrapperStyle}>
          <div className="pricing-card" style={pricingCardStyle}>
            <img src={fivebyfive} alt="5x5 Small Closet" />
            <p style={{ margin: '2px' }} className="a">
              5x5 Unit
            </p>
            <p style={{ margin: '2px', fontSize: '24px' }} className="b">
              Small Closet
            </p>
            <p style={{ margin: '2px' }} className="a">
              <s style={{ fontSize: '24px' }}>$79</s> $30 / month
            </p>
            <p style={{ fontSize: '18px', marginTop: 6 }} className="b">
              <em>Great For Seasonal Storage, Dorm Essentials</em>
            </p>
            <p style={{ fontSize: '16px', marginTop: 4 }} className="b">
              <strong>What Can Fit?</strong> Up to 8 medium boxes, mini fridge, appliances,
              small shelves, luggage.
            </p>
            <div className="buttonContainer" style={{ marginTop: 14, marginBottom: 14 }}>
              <button type="button" className="button1" onClick={() => openModal('5x5')}>
                Reserve
              </button>
            </div>
          </div>

          <div className="pricing-card" style={pricingCardStyle}>
            <img src={fivebyten} alt="5x10 Large Closet" />
            <p style={{ margin: '2px' }} className="a">
              5x10 Unit
            </p>
            <p style={{ margin: '2px', fontSize: '24px' }} className="b">
              Large Closet
            </p>
            <p style={{ margin: '2px' }} className="a">
              <s style={{ fontSize: '24px' }}>$129</s> $50 / month
            </p>
            <p style={{ fontSize: '18px', marginTop: 6 }} className="b">
              <em>Great For Furniture, Summer Dorm Storage</em>
            </p>
            <p style={{ fontSize: '16px', marginTop: 4 }} className="b">
              <strong>What Can Fit?</strong> Up to 16 large boxes, desk, bed frame, chair,
              dresser, TV, other small furniture.
            </p>
            <div className="buttonContainer" style={{ marginTop: 14 }}>
              <button type="button" className="button1" onClick={() => openModal('5x10')}>
                Reserve
              </button>
            </div>
          </div>
        </div>

        <h2 className="c" style={{ marginTop: 32 }}>
          Compare Rates
        </h2>

        <div style={compareCardOuter}>
          <div style={compareCardInner}>
            <div
              style={{
                fontSize: 14,
                opacity: 0.85,
                marginBottom: 8,
              }}
            >
              See how Bins stacks up against traditional storage options.
            </div>

            <div style={tableWrapper}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Company</th>
                    <th style={thStyle}>5x5 Unit</th>
                    <th style={thStyle}>5x10 Unit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={binsRowStyle}>
                    <td style={tdStyle}>
                      <strong>Bins</strong>
                      <span style={badgeStyle}>Best value</span>
                    </td>
                    <td style={tdStyle}>
                      <s style={{ fontSize: '14px', opacity: 0.8 }}>$79</s>
                      <strong> $30</strong>
                    </td>
                    <td style={tdStyle}>
                      <s style={{ fontSize: '14px', opacity: 0.8 }}>$129</s>
                      <strong> $50</strong>
                    </td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>Public Storage</td>
                    <td style={tdStyle}>$89</td>
                    <td style={tdStyle}>$145</td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>Clutter</td>
                    <td style={tdStyle}>$85</td>
                    <td style={tdStyle}>$140</td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>Extra Space Storage</td>
                    <td style={tdStyle}>$95</td>
                    <td style={tdStyle}>$160</td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>CubeSmart</td>
                    <td style={tdStyle}>$92</td>
                    <td style={tdStyle}>$155</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              style={{
                marginTop: 12,
                fontSize: 12,
                opacity: 0.75,
              }}
            >
              Prices shown are representative examples and may vary by location and
              availability.
            </div>
          </div>
        </div>

        {/* Removed app / QR code block per your request */}
      </section>

      {/* Reservation Modal */}
      {showModal && (
        <div style={overlayStyle} onClick={closeModal}>
          <div
            style={modalStyle}
            onClick={(e) => e.stopPropagation()} // keep clicks inside from closing
          >
            {!isSubmitted ? (
              <>
                <div style={modalTitle}>Reserve your {selectedUnit} unit</div>
                <div style={modalSubtitle}>
                  Share a few details and we&apos;ll hold a unit for you. You can finish
                  the full sign up on the next step.
                </div>

                <form onSubmit={handleSubmit}>
                  <div style={modalFieldWrapper}>
                    <TextInput
                      placeholder="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '16px',
                      }}
                    />
                  </div>

                  <div style={modalFieldWrapper}>
                    <TextInput
                      type="email"
                      placeholder="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '16px',
                      }}
                    />
                  </div>

                  <div style={modalFieldWrapper}>
                    <TextInput
                      placeholder="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      title="Please enter a 10-digit phone number"
                      style={{
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '16px',
                      }}
                    />
                  </div>

                  <div style={modalFieldWrapper}>
                    <select
                      name="region"
                      value={formData.region}
                      onChange={handleChange}
                      required
                      style={regionSelectStyle(formData.region)}
                    >
                      <option value="" disabled style={regionOptionStyle}>Choose your region</option>
                      {FACILITY_KEYS.map(key => (
                        <option key={key} value={key} style={regionOptionStyle}>{FACILITIES[key].label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={modalFieldWrapper}>
                    <select
                      name="unitSize"
                      value={selectedUnit || ''}
                      onChange={(e) => setSelectedUnit(e.target.value)}
                      required
                      style={regionSelectStyle(selectedUnit)}
                    >
                      <option value="" disabled style={regionOptionStyle}>Choose a unit size</option>
                      <option value="5x5" style={regionOptionStyle}>5x5 — Small Closet</option>
                      <option value="5x10" style={regionOptionStyle}>5x10 — Large Closet</option>
                    </select>
                  </div>

                  <div style={modalFieldWrapper}>
                    <TextInput
                      placeholder="Preferred Move-in Date"
                      name="moveInDate"
                      value={formData.moveInDate}
                      onChange={handleChange}
                      required
                      style={{
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '16px',
                      }}
                    />
                  </div>

                  <div style={modalFieldWrapper}>
                    <TextArea
                      placeholder="Anything we should know? (optional)"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      style={{
                        fontFamily: 'Raleway, sans-serif',
                        fontSize: '16px',
                      }}
                    />
                  </div>

                  <div style={{ marginTop: 20, textAlign: 'right' }}>
                    <Button
                      className="button1"
                      type="submit"
                      primary
                      disabled={isSubmitting}
                      label={isSubmitting ? 'Reserving…' : 'Reserve'}
                    />
                  </div>
                </form>

                <div
                  style={{
                    marginTop: 12,
                    fontSize: 11,
                    opacity: 0.7,
                    textAlign: 'center',
                  }}
                >
                  By reserving, you&apos;re not committing to payment yet. We&apos;ll
                  follow up to confirm details and finalize your storage plan.
                </div>

                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <button style={subtleButton} type="button" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={modalTitle}>Reservation received 🎉</div>
                <div style={modalSubtitle}>
                  Thanks! We&apos;ll reach out shortly to confirm your {selectedUnit} unit.
                </div>

                <div style={{ fontSize: 12, opacity: 0.6, textAlign: 'center', marginBottom: 16 }}>
                  Redirecting to sign up in a few seconds…
                </div>

                <button
                  type="button"
                  onClick={goToSignup}
                  style={{
                    width: '100%',
                    padding: '13px 16px',
                    borderRadius: 8,
                    border: 'none',
                    backgroundColor: '#7B1FA2',
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginBottom: 12,
                  }}
                >
                  Complete sign up {'→'}
                </button>

                <div style={{ textAlign: 'center' }}>
                  <button type="button" style={subtleButton} onClick={closeModal}>
                    Not now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Download;
